/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft, ArrowRight, Building2, LineChart, ShieldCheck, Users,
    BadgeCheck, Sparkles, Star, MapPin, Lock, Award, Briefcase,
    Heart, ClipboardList, MessageSquare,
} from 'lucide-react';
import { ConvNav } from './landing/ConvNav';
import { ConvFooter } from './landing/ConvFooter';
import { DemoRequestModal } from './landing/DemoRequestModal';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const BENEFITS = [
    {
        icon: ClipboardList,
        title: 'Centralisez le suivi de vos accompagnements',
        desc: "Tous les dossiers vendeurs, visites, échanges et offres consolidés dans un cockpit unique pour l'agence et le groupe.",
    },
    {
        icon: LineChart,
        title: 'Identifiez les priorités et les points de friction',
        desc: "Signaux faibles, mandats à risque, surcharges agents : votre management voit ce qu'il faut traiter en premier, sans micro-management.",
    },
    {
        icon: ShieldCheck,
        title: "Mesurez la qualité du suivi vendeur",
        desc: "Indicateurs métier objectivés sur chaque accompagnement, pour piloter la qualité et la satisfaction côté vendeurs.",
    },
    {
        icon: Users,
        title: 'Valorisez chaque professionnel au sein de l\'équipe',
        desc: "Chaque collaborateur dispose d'une identité professionnelle vérifiée qui rend visible son savoir-faire — au service de l'équipe.",
    },
    {
        icon: Building2,
        title: 'Renforcez la page publique de votre agence',
        desc: "Une vitrine vérifiée, alimentée par le travail réel de vos équipes. L'agence est aussi évaluée à la fin du parcours vendeur.",
    },
    {
        icon: BadgeCheck,
        title: 'Obtenez des avis agence distincts des avis agents',
        desc: "Le vendeur évalue séparément son interlocuteur et l'agence dans son ensemble. Vous capitalisez sur deux niveaux de preuves.",
    },
    {
        icon: Star,
        title: 'Transformez votre travail en preuves de qualité',
        desc: "Chaque dossier suivi devient une preuve — pas un commentaire anonyme — qui alimente la réputation collective.",
    },
    {
        icon: Sparkles,
        title: "Améliorez votre attractivité commerciale et RH",
        desc: "Une agence où les équipes sont reconnues publiquement attire plus de mandats vendeurs et plus de candidatures qualifiées.",
    },
    {
        icon: MessageSquare,
        title: 'Générez des demandes entrantes',
        desc: "Vendeurs et acquéreurs identifient l'agence via la page publique, contactent directement et entrent dans votre cockpit.",
    },
    {
        icon: Lock,
        title: 'Conservez vos données et historiques',
        desc: "Les dossiers, données vendeurs, documents et historiques appartiennent à l'agence et restent dans son cockpit.",
    },
];

const REASSURANCES = [
    "L'agence conserve l'intégralité de ses données opérationnelles.",
    "Les données vendeurs et informations confidentielles ne suivent pas un agent qui quitte l'agence.",
    "Les avis attribués à l'agence restent rattachés à l'agence.",
    "L'historique collectif reste valorisé sur la page agence.",
    "Par défaut, seule l'agence est visible publiquement — pas de page perso à chaque agent.",
    "La visibilité publique d'un agent ne s'active qu'en option, choisie par vous ou par l'agent.",
];

const RH_POINTS = [
    {
        icon: Award,
        title: 'Valorisation de vos collaborateurs',
        desc: 'Chaque professionnel dispose d\'un profil vérifié qui reconnaît publiquement son travail réel.',
    },
    {
        icon: Heart,
        title: 'Marque employeur renforcée',
        desc: 'Une agence dans laquelle on grandit professionnellement et où chaque accompagnement est valorisé.',
    },
    {
        icon: Briefcase,
        title: 'Recrutement de profils qualifiés',
        desc: 'Les candidats vérifient la qualité réelle des équipes en place avant de rejoindre — vous attirez les meilleurs.',
    },
    {
        icon: Sparkles,
        title: 'Avantage professionnel offert',
        desc: 'Vous pouvez prendre en charge le profil personnel enrichi de vos agents comme avantage RH.',
    },
];

export default function PourLesAgencesPage() {
    const [demoOpen, setDemoOpen] = useState(false);

    const scrollToCockpit = (e) => {
        e.preventDefault();
        const el = document.getElementById('cockpit');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="min-h-screen bg-[#050308] text-white antialiased" data-testid="pour-agences-page">
            <ConvNav onDemoClick={() => setDemoOpen(true)} />

            <main className="pt-28 md:pt-36 pb-24">
                {/* ════════ HERO ════════ */}
                <section className="relative overflow-hidden">
                    <div className="pointer-events-none absolute inset-0">
                        <div className="absolute left-1/2 top-0 -translate-x-1/2 w-[700px] h-[600px] rounded-full bg-[#FF914D]/20 blur-[160px]" />
                        <div className="absolute left-1/3 top-40 w-[420px] h-[420px] rounded-full bg-[#681793]/20 blur-[140px]" />
                    </div>

                    <div className="relative max-w-5xl mx-auto px-6 md:px-12">
                        <div className="mb-10">
                            <Link to="/"
                                  className="inline-flex items-center gap-2 text-sm text-[#A19DAB] hover:text-white transition-colors"
                                  data-testid="agences-back-link">
                                <ArrowLeft className="w-4 h-4" />
                                Retour à l'accueil
                            </Link>
                        </div>

                        <motion.span
                            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF914D]/30 bg-[#FF914D]/8 text-[10px] font-medium tracking-widest text-[#FF914D] uppercase"
                            style={MONO}
                        >
                            <Building2 className="w-3 h-3" />
                            Pour les agences et groupes
                        </motion.span>

                        <motion.h1
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]"
                            style={DISPLAY}
                            data-testid="agences-hero-title"
                        >
                            Pilotez la qualité de vos accompagnements.{' '}
                            <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                Transformez-la en réputation collective.
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.12 }}
                            className="mt-6 text-base md:text-lg text-[#A19DAB] leading-relaxed max-w-3xl"
                        >
                            Homly accompagne les agences et les groupes immobiliers pour
                            structurer le suivi vendeur, valoriser le travail de leurs
                            équipes et renforcer la confiance accordée à leur marque.
                            <strong className="text-white"> Par défaut, seule l'agence est
                            visible publiquement.</strong> La visibilité publique d'un agent
                            est une option à votre main : vous décidez qui apparaît et
                            quand.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.18 }}
                            className="mt-9 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                        >
                            <button
                                type="button"
                                onClick={() => setDemoOpen(true)}
                                data-testid="agences-demo-btn"
                                className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold px-7 py-3.5 rounded-full shadow-xl shadow-[#681793]/40 hover:opacity-95 hover:scale-[1.02] transition-all"
                            >
                                Demander une démonstration
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <a
                                href="#cockpit"
                                onClick={scrollToCockpit}
                                data-testid="agences-discover-cockpit"
                                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium px-6 py-3.5 rounded-full border border-white/15 hover:border-white/30 transition-all"
                            >
                                Découvrir le cockpit Homly
                            </a>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="mt-6 text-xs text-[#6E6A7A] flex items-center gap-2"
                        >
                            <ShieldCheck className="w-3.5 h-3.5 text-[#A855F7]" />
                            Sans engagement · Démo personnalisée · 7 à 14 jours pour être opérationnel
                        </motion.p>
                    </div>
                </section>

                {/* ════════ BÉNÉFICES ════════ */}
                <section id="cockpit" className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-6xl mx-auto px-6 md:px-12">
                        <div className="max-w-3xl">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase" style={MONO}>
                                Ce que Homly fait pour vous
                            </span>
                            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.1]" style={DISPLAY}>
                                Un cockpit qui structure.{' '}
                                <span className="text-[#A19DAB]">Une marque qui rayonne.</span>
                            </h2>
                            <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                                Dix bénéfices concrets pour les dirigeants, responsables
                                d'agence et animateurs de réseau.
                            </p>
                        </div>

                        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                            {BENEFITS.map(({ icon: Icon, title, desc }, i) => (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ duration: 0.45, delay: i * 0.04 }}
                                    className="group relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 md:p-6 transition-all duration-500 hover:border-white/25 hover:bg-white/[0.04] hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(255,145,77,0.25)]"
                                    data-testid={`agences-benefit-${i}`}
                                >
                                    <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                         style={{ boxShadow: 'inset 0 1px 0 0 rgba(255,145,77,0.15)' }} />
                                    <div className="relative w-10 h-10 rounded-xl bg-[#FF914D]/12 flex items-center justify-center mb-4 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                                        <Icon className="w-5 h-5 text-[#FF914D]" />
                                    </div>
                                    <h3 className="relative font-semibold text-white mb-2 leading-snug" style={DISPLAY}>
                                        {title}
                                    </h3>
                                    <p className="relative text-sm text-[#A19DAB] leading-relaxed">{desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ════════ RÉASSURANCE ════════ */}
                <section className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-5xl mx-auto px-6 md:px-12">
                        <div className="rounded-3xl border border-[#A855F7]/20 bg-gradient-to-br from-[#A855F7]/8 via-[#1A1224]/40 to-transparent p-8 md:p-12">
                            <div className="flex flex-col md:flex-row md:items-start gap-8">
                                <div className="flex-shrink-0">
                                    <div className="w-14 h-14 rounded-2xl bg-[#A855F7]/15 border border-[#A855F7]/25 flex items-center justify-center">
                                        <Lock className="w-7 h-7 text-[#C7A1F0]" />
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#A855F7]/25 bg-[#A855F7]/10 text-[10px] font-medium tracking-widest text-[#C7A1F0] uppercase" style={MONO}>
                                        Réassurance dirigeant
                                    </span>
                                    <h2 className="mt-4 text-2xl md:text-3xl font-bold tracking-tighter leading-[1.15]" style={DISPLAY}>
                                        L'agence reste au centre. Toujours.
                                    </h2>
                                    <p className="mt-3 text-sm md:text-base text-[#A19DAB] leading-relaxed">
                                        Homly est conçu pour renforcer la relation entre
                                        l'agence et ses agents — pas pour la contourner.
                                    </p>
                                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4" data-testid="agences-reassurances">
                                        {REASSURANCES.map((r) => (
                                            <li key={r} className="flex items-start gap-3 text-sm text-white/85 leading-relaxed">
                                                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C7A1F0]" />
                                                <span>{r}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ════════ ANGLE RH ════════ */}
                <section className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-6xl mx-auto px-6 md:px-12">
                        <div className="max-w-3xl">
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase" style={MONO}>
                                Le profil agent vu par l'agence
                            </span>
                            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.1]" style={DISPLAY}>
                                Reconnaître ses équipes,{' '}
                                <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                    c'est faire grandir son agence.
                                </span>
                            </h2>
                            <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                                Le profil personnel enrichi (19 € HT / mois) est une
                                extension facultative pour les agents qui souhaitent une
                                visibilité personnelle renforcée. <strong className="text-white">L'agence peut
                                en prendre la charge</strong> comme avantage professionnel — la rétention et
                                la marque employeur s'en trouvent renforcées.
                            </p>
                        </div>

                        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {RH_POINTS.map(({ icon: Icon, title, desc }, i) => (
                                <motion.div
                                    key={title}
                                    initial={{ opacity: 0, y: 18 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ duration: 0.45, delay: i * 0.06 }}
                                    className="rounded-2xl border border-white/10 bg-white/[0.02] p-5"
                                    data-testid={`agences-rh-${i}`}
                                >
                                    <div className="w-10 h-10 rounded-xl bg-[#A855F7]/12 flex items-center justify-center mb-4">
                                        <Icon className="w-5 h-5 text-[#C7A1F0]" />
                                    </div>
                                    <h3 className="font-semibold text-white mb-2 text-sm leading-snug" style={DISPLAY}>
                                        {title}
                                    </h3>
                                    <p className="text-sm text-[#A19DAB] leading-relaxed">{desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ════════ CTA FINAL ════════ */}
                <section className="relative mt-24 md:mt-32 border-t border-white/5 pt-20">
                    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-[1.1]" style={DISPLAY}>
                            Voyons ensemble ce que Homly fera{' '}
                            <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                                pour votre agence.
                            </span>
                        </h2>
                        <p className="mt-4 text-base text-[#A19DAB] leading-relaxed">
                            15 minutes pour comprendre votre organisation. Une démo
                            personnalisée. Une vision claire — pas un argumentaire.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                type="button"
                                onClick={() => setDemoOpen(true)}
                                data-testid="agences-final-cta"
                                className="group inline-flex items-center gap-2 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-semibold px-7 py-3.5 rounded-full shadow-xl shadow-[#681793]/40 hover:opacity-95 hover:scale-[1.02] transition-all"
                            >
                                Demander une démonstration
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <Link
                                to="/pour-les-agents"
                                data-testid="agences-cross-link"
                                className="inline-flex items-center gap-2 text-white/90 hover:text-white font-medium px-6 py-3.5 rounded-full border border-white/15 hover:border-white/30 transition-all"
                            >
                                <MapPin className="w-4 h-4 text-[#A855F7]" />
                                Vous êtes agent immobilier ?
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <ConvFooter />
            <DemoRequestModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
        </div>
    );
}
